import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useProducts } from '../ProductContext';
import { apiFetch, apiJson } from '../apiClient';
import { productUnitLabel } from '../constants/productUnits';
import { exportCustomerDocumentPdf, customerDocTypeLabel } from '../utils/brickCustomerDocExport';
import './BrickFactoryPage.css';

const DOC_TYPES = [
  { value: 'quotation', label: 'Quotation' },
  { value: 'sales_order', label: 'Sales order' },
  { value: 'proforma', label: 'Proforma invoice' },
  { value: 'invoice', label: 'Tax invoice' },
];

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTzs(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-TZ', { maximumFractionDigits: 0 });
}

const emptyLine = () => ({ productId: '', quantity: '', unitPriceTzs: '' });

const ShopCustomerDocsPage = ({ onNavigate }) => {
  const { products } = useProducts();
  const sellable = useMemo(
    () => (products || []).filter((p) => p.posSellable !== false),
    [products]
  );

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const [form, setForm] = useState({
    documentType: 'quotation',
    documentDate: todayKey(),
    customerName: '',
    customerPhone: '',
    destination: '',
    notes: '',
  });
  const [lines, setLines] = useState([emptyLine()]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiJson('/customer-documents');
      setDocuments(d.documents || []);
      setMsg(null);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Load failed.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onProductPick = (index, productId) => {
    const p = sellable.find((x) => String(x.id) === String(productId));
    setLines((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        productId,
        unitPriceTzs: p?.sellingPrice != null ? String(p.sellingPrice) : next[index].unitPriceTzs,
      };
      return next;
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const items = lines
        .map((row) => ({
          productId: Number(row.productId),
          quantity: Number(row.quantity),
          unitPriceTzs: row.unitPriceTzs ? Number(row.unitPriceTzs) : null,
        }))
        .filter((row) => Number.isFinite(row.productId) && row.productId > 0 && row.quantity > 0);

      if (!items.length) {
        throw new Error('Add at least one product line with quantity.');
      }

      const res = await apiFetch('/customer-documents', {
        method: 'POST',
        body: JSON.stringify({
          documentType: form.documentType,
          documentDate: form.documentDate,
          customerName: form.customerName.trim(),
          customerPhone: form.customerPhone.trim() || null,
          destination: form.destination.trim() || null,
          notes: form.notes.trim() || null,
          items,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Could not save document.');
      }
      const data = await res.json();
      setDocuments((prev) => [data.document, ...prev]);
      setForm((f) => ({
        ...f,
        customerName: '',
        customerPhone: '',
        destination: '',
        notes: '',
      }));
      setLines([emptyLine()]);
      setMsg({ type: 'success', text: `${customerDocTypeLabel(form.documentType)} saved.` });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="brick-page brick-docs-page">
      <header className="brick-page-header">
        <h1>Quotations &amp; invoices</h1>
        <p className="brick-page-sub">
          Quotation, sales order, proforma, and invoice for your customers — using products from stock.
        </p>
        <button type="button" className="brick-link" onClick={() => onNavigate?.('overview')}>
          ← Overview
        </button>
      </header>

      {msg ? (
        <p className={msg.type === 'error' ? 'brick-msg brick-msg--error' : 'brick-msg brick-msg--ok'}>{msg.text}</p>
      ) : null}

      <section className="brick-split">
        <section className="brick-card">
          <h2>New document</h2>
          {!sellable.length ? (
            <p className="brick-empty">Add approved products in stock first.</p>
          ) : (
            <form className="brick-form" onSubmit={handleSubmit}>
              <label>
                Document type
                <select
                  value={form.documentType}
                  onChange={(e) => setForm((f) => ({ ...f, documentType: e.target.value }))}
                >
                  {DOC_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Date
                <input
                  type="date"
                  required
                  value={form.documentDate}
                  onChange={(e) => setForm((f) => ({ ...f, documentDate: e.target.value }))}
                />
              </label>
              <label>
                Customer name
                <input
                  required
                  value={form.customerName}
                  onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                />
              </label>
              <label>
                Phone
                <input
                  value={form.customerPhone}
                  onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
                />
              </label>
              <label>
                Site / destination
                <input
                  value={form.destination}
                  onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
                  placeholder="e.g. construction site, ward"
                />
              </label>

              <fieldset className="brick-docs-lines">
                <legend>Line items</legend>
                {lines.map((row, index) => {
                  const p = sellable.find((x) => String(x.id) === String(row.productId));
                  return (
                    <div key={index} className="brick-docs-line">
                      <label>
                        Product
                        <select
                          required
                          value={row.productId}
                          onChange={(e) => onProductPick(index, e.target.value)}
                        >
                          <option value="">Select…</option>
                          {sellable.map((prod) => (
                            <option key={prod.id} value={prod.id}>
                              {prod.name}
                              {prod.unit ? ` (${productUnitLabel(prod.unit)})` : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Qty{p?.unit ? ` (${productUnitLabel(p.unit)})` : ''}
                        <input
                          type="number"
                          min="1"
                          required
                          value={row.quantity}
                          onChange={(e) => {
                            const v = e.target.value;
                            setLines((prev) => {
                              const next = [...prev];
                              next[index] = { ...next[index], quantity: v };
                              return next;
                            });
                          }}
                        />
                      </label>
                      <label>
                        Unit price (TZS)
                        <input
                          type="number"
                          min="0"
                          value={row.unitPriceTzs}
                          onChange={(e) => {
                            const v = e.target.value;
                            setLines((prev) => {
                              const next = [...prev];
                              next[index] = { ...next[index], unitPriceTzs: v };
                              return next;
                            });
                          }}
                        />
                      </label>
                      {lines.length > 1 ? (
                        <button type="button" className="brick-link" onClick={() => removeLine(index)}>
                          Remove line
                        </button>
                      ) : null}
                    </div>
                  );
                })}
                <button type="button" className="brick-link" onClick={addLine}>
                  + Add another product
                </button>
              </fieldset>

              <label>
                Notes
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
              <button type="submit" className="brick-btn brick-btn--primary" disabled={busy}>
                Save document
              </button>
            </form>
          )}
        </section>

        <section className="brick-card brick-card--grow">
          <h2>Recent documents</h2>
          {loading ? <p className="brick-loading">Loading…</p> : null}
          {!loading && !documents.length ? <p className="brick-empty">No documents yet.</p> : null}
          {documents.length ? (
            <section className="brick-table-wrap">
              <table className="brick-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Lines</th>
                    <th>Total</th>
                    <th>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td>{doc.documentNumber}</td>
                      <td>{customerDocTypeLabel(doc.documentType)}</td>
                      <td>{doc.documentDate}</td>
                      <td>{doc.customerName}</td>
                      <td>{doc.items?.length ?? 0}</td>
                      <td>{formatTzs(doc.totalTzs)}</td>
                      <td>
                        <button
                          type="button"
                          className="brick-link"
                          onClick={() => void exportCustomerDocumentPdf(doc)}
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}
        </section>
      </section>
    </section>
  );
};

export default ShopCustomerDocsPage;
